/**
 * DownloadAppSection — landing-page block that promotes PWA installation.
 *
 *  • Desktop (no touch / wide screen)   → renders a high-contrast QR pointing
 *    to `${origin}/?source=pwa-qr`. Scanning on a phone takes the visitor
 *    straight to the app; once installed, the `?source=pwa-qr` analytic tag
 *    survives on the launcher.
 *  • Mobile (touch / narrow screen)     → renders a one-tap "Install" CTA.
 *    On Android Chrome it triggers the captured `beforeinstallprompt`; on
 *    iOS Safari it shows the same Share → Add-to-Home instructions card.
 *  • Already-installed PWA              → renders a celebratory "You're in"
 *    badge so we don't try to re-install.
 */
import React, { useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Smartphone, Download, Apple, Share, Plus, CheckCircle2 } from "lucide-react";

const QR_DARK = "#05073B";   // navy
const QR_LIGHT = "#FFFFFF";  // background

function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(max-width: 1024px)").matches ||
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  return (
    /iphone|ipad|ipod/.test(ua) ||
    (ua.includes("mac") && navigator.maxTouchPoints > 1)
  );
}

export function DownloadAppSection() {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [qrUrl, setQrUrl] = useState("");
  const [mobile, setMobile] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [iOS, setIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useImperativeLoad((() => () => {
    // Compute target URL — always use the live origin, not the preview
    // domain, so QR codes scanned later still resolve correctly.
    const origin = window.location.hostname.endsWith("neuracom.ai")
      ? window.location.origin
      : "https://neuracom.ai";
    const url = `${origin}/?source=pwa-qr`;
    setQrUrl(url);

    setMobile(isMobileDevice());
    setStandalone(isStandalone());
    setIOS(isIOS());

    // Render the QR onto the canvas (desktop only, but cheap on mobile too)
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 260,
        margin: 1,
        color: { dark: QR_DARK, light: QR_LIGHT },
        errorCorrectionLevel: "M",
      }).catch(() => {
        /* qrcode failed — non-fatal, the card still shows the URL */
      });
    }

    // Capture the install prompt for one-tap install on Android
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
  })(), []);

  const handleInstall = async () => {
    if (iOS) {
      setShowIOSInstructions(true);
      return;
    }
    if (!deferredPrompt) {
      // No native prompt yet (eg. user hasn't engaged enough) — fall back to
      // brief instructions card.
      setShowIOSInstructions(true);
      return;
    }
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  // ── Already installed → minimal celebratory state ─────────────────────────
  if (standalone) {
    return (
      <section
        id="download"
        className="relative bg-[#05073B] py-20"
        data-testid="landing-download-section"
      >
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/40 bg-[#9ACD32]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#9ACD32]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("download.installed_badge")}
          </span>
          <h2 className="mt-5 font-display text-3xl font-extrabold text-white sm:text-4xl">
            {t("download.installed_title")}
          </h2>
          <p className="mt-3 text-slate-400">{t("download.installed_desc")}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="download"
      className="relative overflow-hidden bg-[#05073B] py-24 lg:py-28"
      data-testid="landing-download-section"
    >
      {/* Subtle diagonal accent */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(154,205,50,0.08),transparent_50%)]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.1fr_1fr] lg:gap-20 lg:px-10">
        {/* Left — copy + CTAs */}
        <div>
          <span
            className="inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/40 bg-[#9ACD32]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#9ACD32]"
            data-testid="download-pretitle"
          >
            <Smartphone className="h-3.5 w-3.5" />
            {t("download.pretitle")}
          </span>
          <h2
            className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="download-title"
          >
            {t("download.title")}
          </h2>
          <p
            className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300"
            data-testid="download-subtitle"
          >
            {mobile ? t("download.subtitle_mobile") : t("download.subtitle_desktop")}
          </p>

          {/* Benefit chips */}
          <ul className="mt-8 flex flex-wrap gap-3">
            {[
              "download.chip_no_store",
              "download.chip_offline",
              "download.chip_native",
              "download.chip_size",
            ].map((k) => (
              <li
                key={k}
                className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300"
                data-testid={`download-chip-${k.split(".")[1].replace(/^chip_/, "")}`}
              >
                {t(k)}
              </li>
            ))}
          </ul>

          {/* Mobile CTA — direct install */}
          {mobile && !showIOSInstructions && (
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                onClick={handleInstall}
                size="lg"
                className="min-h-[44px] rounded-full bg-[#9ACD32] px-7 font-bold text-[#05073B] shadow-[0_8px_32px_rgba(154,205,50,0.35)] hover:bg-[#A8DF38]"
                data-testid="download-install-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                {t("download.install_now")}
              </Button>
              <span className="text-xs uppercase tracking-wider text-slate-500">
                {t("download.no_store_note")}
              </span>
            </div>
          )}

          {/* iOS instructions (shown after CTA tap on iOS or when no prompt) */}
          {mobile && showIOSInstructions && (
            <div
              className="mt-8 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/5 p-5"
              data-testid="download-ios-instructions"
            >
              <div className="flex items-center gap-2 text-sm font-bold text-[#9ACD32]">
                <Apple className="h-4 w-4" />
                {t("download.ios_title")}
              </div>
              <ol className="mt-3 space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] text-[10px] font-bold text-[#05073B]">1</span>
                  <span>
                    {t("download.ios_step_1")}{" "}
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-800">
                      <Share className="h-3 w-3" />
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] text-[10px] font-bold text-[#05073B]">2</span>
                  <span>
                    {t("download.ios_step_2")}{" "}
                    <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 text-xs">
                      <Plus className="h-3 w-3" /> {t("download.ios_add_to_home")}
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] text-[10px] font-bold text-[#05073B]">3</span>
                  <span>{t("download.ios_step_3")}</span>
                </li>
              </ol>
            </div>
          )}

          {/* Desktop subtitle hint */}
          {!mobile && (
            <p className="mt-8 text-sm text-slate-500" data-testid="download-desktop-note">
              {t("download.desktop_note")}
            </p>
          )}
        </div>

        {/* Right — QR card */}
        <div className="relative" data-testid="download-qr-block">
          <div className="relative mx-auto w-fit">
            {/* Glowing accent ring */}
            <div className="absolute -inset-3 rounded-3xl bg-[#9ACD32]/20 blur-2xl" />
            <div className="relative rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-slate-200">
              <canvas
                ref={canvasRef}
                className="block"
                data-testid="download-qr-canvas"
              />
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="font-mono uppercase tracking-wider text-slate-500">
                  neuracom.ai
                </span>
                <img
                  src="/brand/pwa/icon-72.png"
                  alt="NEURACOM"
                  className="h-7 w-7 rounded-md"
                />
              </div>
            </div>
            <p
              className="mt-5 max-w-[260px] text-center text-xs uppercase tracking-[0.16em] text-slate-500 mx-auto"
              data-testid="download-qr-caption"
            >
              {mobile ? t("download.qr_caption_mobile") : t("download.qr_caption_desktop")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
